import { useMsal } from "@azure/msal-react";
import { CommandBar, ICommandBarItemProps, IContextualMenuItem, Stack, useTheme } from "@fluentui/react";
import { RootState } from "app/store";
import useHiddenWithMount from "components/useHiddenWithMount";
import saveAs from "file-saver";
import { AppLinkToExternal, emitCustomEvent } from "global/Application";
import { SaveQuoteRequestAsGroupConfigurationAction, UpdateOpportunityProductAction, loadOpportunityProductAction, saveOpportunityProductDataAction } from "global/Application/dataActions";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { addUpdateOpportunityProductOption } from "global/Reducer/dataSlice";
import { setIsNavPinned, setProcessing, setShowDeactivated } from "global/Reducer/statusSlice";
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import OptionWizard from "./OptionWizard";
import QuoteRequestContext from "./QuoteRequestContext";
import QuoteRequestItem from "./QuoteRequestItem";

interface IOpportunityProductDataProps {

}

const QuoteRequest: React.FunctionComponent<IOpportunityProductDataProps> = (props) => {

    const theme = useTheme();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);
    const history = useHistory();

    const state = useSelector((state: RootState) => state);
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = `Quote Request | ${state.data.opportunityProduct?.bcbs_quoterequest?.bcbs_name ?? ""}`;
    }, [state.data.opportunityProduct?.bcbs_quoterequest]);

    /*
    INSERT INTO bcbs_copaycoinoption_config_catego (bcbs_config_categoryid, bcbs_copaycoinoptionid)
    SELECT DISTINCT t.bcbs_config_categoryid, copays.bcbs_copaycoinoptionid
    FROM bcbs_copaycoinoption_config_catego copays
    JOIN bcbs_config_category cat on cat.bcbs_config_categoryid = copays.bcbs_config_categoryid
    CROSS APPLY (SELECT TOP 1 * FROM bcbs_config_category cat2 WHERE cat2.bcbs_name = 'Quote Request') T
    WHERE cat.bcbs_name = 'Medical'
    */

    // Styles definition


    const menuItems = useMemo(() => {
        const items: ICommandBarItemProps[] = []

        items.push({
            key: 'new',
            text: 'New',
            iconProps: { iconName: 'FabricNewFolder' },
            onClick(ev, item) {
                setOptionWizardHidden(false);
            },
        });

        items.push({
            key: 'save',
            text: 'Save',
            iconProps: { iconName: 'Save' },
            onClick(ev, item) {
                dispatch(saveOpportunityProductDataAction(dataSource));
            },

        });

        items.push({
            key: 'navigate',
            text: 'Navigate',
            iconProps: { iconName: 'NavigateForward' },

            subMenuProps: {
                items: state.data.opportunityProduct?.options.filter(t => !t.deactivated).map((option, index) => {
                    const t: IContextualMenuItem = {
                        key: option.id,
                        text: option.name,
                        onClick(ev, item) {
                            history.push(`/bcbs_opportunityproduct/${state.data.opportunityProduct?.bcbs_opportunityproduct.bcbs_opportunityproductid}/bcbs_opportunityoption/${option.id}`); // Add this line

                            const element = document.getElementById("anchor-" + option.id);
                            if (element) {
                                element.scrollIntoView({ behavior: "smooth" });
                            }
                        },
                    }
                    return t;
                }) ?? []
            }

        });

        let selected = state.data.opportunityProduct?.options.filter(option => option.select) ?? [];

        if (selected.length === 0)
            selected = state.data.opportunityProduct?.options.filter(option => !option.deactivated) ?? [];

        items.push({
            key: 'compare',
            disabled: selected.length < 1,
            text: selected?.length > 1
                ? `Compare (${selected?.length} selected)`
                : 'Export'
            ,
            iconProps: { iconName: 'Compare' },
            onClick(ev, item) {

                (async () => {
                    try {
                        dispatch(setProcessing(true));
                        const excelExtractImport = await import("global/ExcelExtract")
                        const { OpportunityProductBenefitCompareGeneration } = excelExtractImport;
                        const result = await OpportunityProductBenefitCompareGeneration(state, dataSource, { options: selected, isCompare: selected?.length > 1 })
                        const fileName = `OpportunityProductOptionCompare-${new Date().getTime()}.xlsx`;
                        saveAs(result.file, fileName);
                    } catch (error) {
                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });
                    } finally {
                        dispatch(setProcessing(false));
                    }
                })();


            },
        });

        const wonOptions = state.data.opportunityProduct?.options.filter(t => t.won) ?? [];
        // const isNewBusiness = state.data.opportunityProduct?.opportunity.bcbs_opportunitytype === opportunity.bcbs_opportunitytype["New Business(808900000)"].value;

        items.push({
            key: 'Generate Snapshot',
            text: wonOptions.length > 0
                ? `Generate Snapshot (${wonOptions.length} selected)`
                : `Generate Snapshot`,
            iconProps: {
                iconName: 'ModelingView',
            },
            disabled: true,
            // disabled: !isNewBusiness || wonOptions.length === 0,
            onClick: () => {
                dispatch(SaveQuoteRequestAsGroupConfigurationAction(dataSource, wonOptions))
            }
        });

        items.push({
            key: 'showDeactivated',
            text: 'Show Deactivated',
            iconProps: {
                iconName: state.status.showDeactivated ? 'RedEye' : 'Hide',
                styles: {
                }
            },
            buttonStyles: {
                root: {
                    backgroundColor: !state.status.showDeactivated ? undefined : theme.semanticColors.warningBackground,
                }
            },
            onClick: () => {
                dispatch(setShowDeactivated(!state.status.showDeactivated));
            }
        });

        if (state.data.opportunityProduct?.options.some(t => !t.deactivated && (t.requiresUpdate ?? false)))
            items.push({
                key: 'update',
                text: 'Update',
                iconProps: { iconName: 'LightningBolt', },
                onClick: () => {
                    dispatch(UpdateOpportunityProductAction(dataSource));
                }
            });

        return items;
    }, [state.data.opportunityProduct?.options, state.status.showDeactivated]);

    const farMenuItems = useMemo(() => {
        const farMenuItems: ICommandBarItemProps[] = []

        farMenuItems.push({
            key: 'refresh',
            iconProps: { iconName: 'Refresh' },
            text: 'Refresh',
            onClick(ev, item) {
                dispatch(
                    loadOpportunityProductAction(dataSource, state.data.opportunityProduct?.bcbs_opportunityproduct?.bcbs_opportunityproductid!)
                );
            },
        });


        farMenuItems.push({
            key: 'expand',
            text: 'Full Screen',
            iconProps: { iconName: 'MiniExpand' },
            onClick(ev, item) {

                if (state.data.opportunityProduct?.bcbs_opportunityproduct?.bcbs_opportunityproductid)
                    window.open(
                        AppLinkToExternal({
                            id: state.data.opportunityProduct.bcbs_opportunityproduct.bcbs_opportunityproductid,
                            type: 'groupLibrary:bcbs_opportunityproduct'
                        }),
                        '_blank'
                    );
            },
        });

        farMenuItems.push({
            key: 'pin',
            iconProps: { iconName: state.status.isNavPinned ? 'PinnedSolid' : 'Pin' },
            onClick(ev, item) {
                dispatch(setIsNavPinned());
            }
        });

        return farMenuItems;
    }, [
        state.data?.opportunityProduct?.bcbs_opportunityproduct,
        state.status.isNavPinned,
        state.status.showDeactivated
    ]);

    const dtRef = useRef<HTMLDivElement>(null);

    const stickyStyle: React.CSSProperties = {
        position: 'sticky',
        left: '16px',
        zIndex: 3,
        display: 'inline-block',
    }

    const [optionWizardHidden, setOptionWizardHidden, optionWizardMounted] = useHiddenWithMount(true);

    if (state.status.isNavPinned)
        stickyStyle.top = '0';

    return <QuoteRequestContext>
        <div
            style={stickyStyle}
        >
            <CommandBar
                styles={{
                    root: {
                        backgroundColor: theme.palette.white,
                        boxShadow: theme.effects.elevation4,
                        borderRadius: '5px',
                        width: 'calc(100vw - 64px - 32px)',
                    }
                }}
                items={menuItems}
                farItems={farMenuItems}
            />

        </div>
        <div
            ref={dtRef}
            style={{
                marginTop: '10px',
                // overflowX: 'scroll',
                // overflowY: 'scroll',
                // height: `calc(100vh - ${dtRef.current?.offsetTop ?? 200}px - 10px)`,
            }}
        >
            <Stack
                horizontal
                disableShrink={true}
                tokens={{ childrenGap: '15px' }}
            >

                {state.data.opportunityProduct?.options.map((option, index) => {

                    // skip deactivated records/only show deactivated records if permitted at group config properties level
                    if ((option.deactivated ?? false) !== state.status.showDeactivated)
                        return;

                    return (
                        <QuoteRequestItem
                            key={index}
                            optionBenefit={option}
                            opportunityProduct={state.data.opportunityProduct!}
                        />
                    );
                })}
            </Stack>
        </div>
        {state.status.processing &&
            <PageLoading />
        }

        {optionWizardMounted &&
            <OptionWizard
                hidden={optionWizardHidden}
                onChange={(tierChange) => {
                    setOptionWizardHidden(true);
                    if (tierChange)
                        dispatch(addUpdateOpportunityProductOption({
                            tierChange: tierChange
                        }));
                }}
                onDismiss={() => setOptionWizardHidden(true)}
            />
        }

    </QuoteRequestContext>

};

export default QuoteRequest;